import { createContext, useContext, useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import { Heading, PageHeader, Text } from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'

import layoutStyles from '../HomeLayout/HomeLayout.module.css'
import styles from './GreetingHeader.module.css'

function getTimeOfDayGreeting(date: Date): { emoji: string; greeting: string } {
  const hour = date.getHours()

  if (hour >= 18 || hour < 4) {
    return { emoji: '🌙', greeting: 'Good Evening' }
  }
  if (hour < 12) {
    return { emoji: '☀️', greeting: 'Good Morning' }
  }
  return { emoji: '👋', greeting: 'Good Afternoon' }
}

function getFirstName(fullName: string | undefined | null): string | null {
  if (!fullName) return null
  return fullName.trim().split(' ')[0] ?? null
}

function formatTodayLabel(date: Date): string {
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' })
  const month = date.toLocaleDateString('en-US', { month: 'short' })
  return `${weekday}, ${month} ${date.getDate()} ${date.getFullYear()}`
}

type GreetingHeaderContextValue = {
  heading: string
  dateLabel: string
  emoji: string
}

const GreetingHeaderContext = createContext<GreetingHeaderContextValue | null>(
  null,
)

function useGreetingHeaderContext(): GreetingHeaderContextValue {
  const context = useContext(GreetingHeaderContext)
  if (context === null) {
    throw new Error(`Component must be rendered within <GreetingHeader>`)
  }
  return context
}

/**
 * The greeting tile — a rounded-square that centres the time-of-day emoji.
 */
function Avatar({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className={styles.avatar}>
      <Heading as="p" size="xl">
        {children}
      </Heading>
    </div>
  )
}

/**
 * Home page header, composed as a compound component:
 *
 * ```tsx
 * <GreetingHeader>
 *   <GreetingHeader.Group>
 *     <GreetingHeader.Title />
 *     <GreetingHeader.SubTitle />
 *   </GreetingHeader.Group>
 *   <GreetingHeader.Actions>…</GreetingHeader.Actions>
 * </GreetingHeader>
 * ```
 *
 * The root computes the time-of-day greeting and today's date once and shares
 * them with the subcomponents via context. `Group` renders the emoji avatar
 * tile alongside the `Title` / `SubTitle` it wraps.
 */
function GreetingHeader({ children }: { children: ReactNode }): JSX.Element {
  const { account } = useAccount()
  const firstName = getFirstName(account?.name)
  const now = new Date()
  const { emoji, greeting } = getTimeOfDayGreeting(now)
  const heading = firstName ? `${greeting}, ${firstName}!` : `${greeting}!`
  const dateLabel = formatTodayLabel(now)

  const contextValue = useMemo(
    () => ({ heading, dateLabel, emoji }),
    [heading, dateLabel, emoji],
  )

  return (
    <GreetingHeaderContext.Provider value={contextValue}>
      <PageHeader separator={false} className={layoutStyles.greeting}>
        <PageHeader.Row size="full" className={layoutStyles.greetingRow}>
          {children}
        </PageHeader.Row>
      </PageHeader>
    </GreetingHeaderContext.Provider>
  )
}

function GreetingHeaderTitleGroup({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  const { emoji } = useGreetingHeaderContext()
  return (
    <PageHeader.TitleGroup>
      <PageHeader.TitleLeading>
        <Avatar>{emoji}</Avatar>
      </PageHeader.TitleLeading>
      <div>{children}</div>
    </PageHeader.TitleGroup>
  )
}
GreetingHeaderTitleGroup.displayName = 'GreetingHeader.Group'

/**
 * The greeting heading (`<h1>`). Renders the computed time-of-day greeting;
 * `truncate={false}` lets it wrap instead of clipping on narrow screens.
 */
function GreetingHeaderTitle(): JSX.Element {
  const { heading } = useGreetingHeaderContext()
  return (
    <PageHeader.Title size="lg" truncate={false}>
      {heading}
    </PageHeader.Title>
  )
}
GreetingHeaderTitle.displayName = 'GreetingHeader.Title'

/** The date line rendered below the greeting. */
function GreetingHeaderSubTitle(): JSX.Element {
  const { dateLabel } = useGreetingHeaderContext()
  return (
    <Text as="p" weight="medium" color="subtle">
      {dateLabel}
    </Text>
  )
}
GreetingHeaderSubTitle.displayName = 'GreetingHeader.SubTitle'

/** The trailing action cluster, aligned to the end of the header. */
function GreetingHeaderActions({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  return <PageHeader.Actions>{children}</PageHeader.Actions>
}
GreetingHeaderActions.displayName = 'GreetingHeader.Actions'

GreetingHeader.Group = GreetingHeaderTitleGroup
GreetingHeader.Title = GreetingHeaderTitle
GreetingHeader.SubTitle = GreetingHeaderSubTitle
GreetingHeader.Actions = GreetingHeaderActions

export { GreetingHeader }
